import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Button, IconButton } from '@mui/material';
import { Close, RocketLaunch } from '@mui/icons-material';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import { useAuth } from '../../auth/useAuth';
import { useOnboarding } from '../OnboardingProvider';
import { useRBAC } from '../../auth/useRBAC';
import { pickTourForRole } from '../tours';
import { withOfflineFallback, readCurrentUser } from '../../offline';

const WELCOME_SEEN_KEY = 'onboardingWelcomeSeen';
const FIRST_LOGIN_WINDOW_MS = 5 * 60 * 1000; // 5 min

interface BackendUser {
  firstName?: string;
  lastName?: string;
  professionalTitle?: string;
  name?: string;
}

/**
 * Shows once on a user's first real login to the app. Gated on firstLoginAt
 * being set within the last 5 minutes AND the local "seen" flag being absent,
 * so refreshing the page mid-session doesn't re-trigger it.
 */
export const WelcomeModal: React.FC = () => {
  const { state, openDrawer, startTour } = useOnboarding();
  const { user } = useAuth();
  const { claims } = useRBAC();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const roleTour = pickTourForRole(claims?.role);

  // users is a replicated table — offline, the greeting comes from the replica.
  const { data: backendUser } = useQuery<BackendUser>(
    ['current-user-me'],
    withOfflineFallback(
      async () => {
        const res = await api.get('/users/me');
        return res.data;
      },
      () => readCurrentUser(user?.uid, user?.email ?? undefined) as Promise<BackendUser>,
    ),
    { staleTime: 5 * 60 * 1000 },
  );

  useEffect(() => {
    if (!state?.firstLoginAt || dismissed) return;
    const seen = typeof window !== 'undefined' && localStorage.getItem(WELCOME_SEEN_KEY);
    if (seen) return;
    const firstLoginTs = new Date(state.firstLoginAt).getTime();
    if (Date.now() - firstLoginTs > FIRST_LOGIN_WINDOW_MS) return;
    // Welcome takes priority on first login — release notes defers to us.
    setOpen(true);
  }, [state?.firstLoginAt, dismissed]);

  const markSeen = () => {
    try {
      localStorage.setItem(WELCOME_SEEN_KEY, 'true');
    } catch {
      /* quota / privacy mode — non-blocking */
    }
    setDismissed(true);
    setOpen(false);
  };

  const handleTakeTour = () => {
    markSeen();
    if (roleTour) {
      startTour(roleTour.id);
    } else {
      openDrawer();
    }
  };

  const handleOpenChecklist = () => {
    markSeen();
    openDrawer();
  };

  const nameForGreeting = (() => {
    if (backendUser?.lastName) {
      const title = backendUser.professionalTitle || 'Dr.';
      return `${title} ${backendUser.lastName}`;
    }
    if (backendUser?.firstName) return backendUser.firstName;
    return 'there';
  })();

  const clinicFragment = '';

  return (
    <Dialog
      open={open}
      onClose={markSeen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: '#FAF7F1',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ position: 'relative', bgcolor: '#8B4513', px: 4, py: 5, color: '#FFFFFF' }}>
        <IconButton
          onClick={markSeen}
          aria-label="Close welcome"
          sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.8)' }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <RocketLaunch sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, lineHeight: 1.2, color: '#FFFFFF' }}
            >
              Welcome to StableTrack, {nameForGreeting}.
            </Typography>
            <Typography sx={{ mt: 0.5, color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>
              Let's get your clinic running{clinicFragment} in about ten minutes.
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 4, py: 3.5 }}>
        <Typography variant="body2" sx={{ color: '#3B2E1A', lineHeight: 1.6, mb: 1.5 }}>
          We'll walk you through twelve short steps — from branding your invoices and
          connecting Stripe, to documenting your first SOAP note and sending a discharge
          summary. Everything auto-ticks as you go. You can stop and resume anytime.
        </Typography>
        <Typography variant="caption" sx={{ color: '#6B6050', fontStyle: 'italic' }}>
          Built for equine practices.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            onClick={markSeen}
            variant="text"
            sx={{
              color: '#8B6B4A',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(139,107,74,0.08)' },
            }}
          >
            I'll explore on my own
          </Button>
          {roleTour && (
            <Button
              onClick={handleOpenChecklist}
              variant="outlined"
              sx={{
                borderColor: '#C4A478',
                color: '#8B4513',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                '&:hover': {
                  borderColor: '#8B4513',
                  bgcolor: 'rgba(139,69,19,0.04)',
                },
              }}
            >
              Show checklist
            </Button>
          )}
          <Button
            onClick={handleTakeTour}
            variant="contained"
            sx={{
              bgcolor: '#8B4513',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              boxShadow: '0 2px 4px rgba(139,69,19,0.2)',
              '&:hover': {
                bgcolor: '#6B4F2E',
                boxShadow: '0 4px 8px rgba(139,69,19,0.3)',
              },
            }}
          >
            {roleTour ? 'Take a quick tour' : 'Show me the checklist'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
